import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserPlus, Search, Filter, Download,
  Mail, Phone, Building2, Calendar, Clock,
  CheckCircle2, XCircle, AlertTriangle, Star,
  FileText, MessageSquare, UserCheck, Plus,
  ChevronRight, BarChart2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { employeeData } from '../data/mockData';
import AddCandidateForm from './AddCandidateForm';
import ScheduleInterviewForm from './ScheduleInterviewForm';

const CandidateCard = ({ candidate }) => {
  const statusColors = {
    'Screening': 'bg-blue-100 text-blue-700',
    'Interview': 'bg-purple-100 text-purple-700',
    'Technical': 'bg-amber-100 text-amber-700',
    'Offer': 'bg-green-100 text-green-700',
    'Rejected': 'bg-red-100 text-red-700'
  };

  const [showScheduleForm, setShowScheduleForm] = useState(false);

  const handleScheduleInterview = (interviewData) => {
    console.log('Scheduling interview:', interviewData);
    setShowScheduleForm(false);
  };

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-xl p-6 shadow-sm"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <span className="text-lg font-semibold text-indigo-600">
              {candidate.name.split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{candidate.name}</h3>
            <p className="text-sm text-gray-500">{candidate.position}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[candidate.status]}`}>
          {candidate.status}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Mail className="w-4 h-4" />
          {candidate.email}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Phone className="w-4 h-4" />
          {candidate.phone}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4" />
          {candidate.department}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          {candidate.appliedDate}
        </div>
      </div>

      <div className="flex gap-2 pt-4 border-t">
        <button
          onClick={() => setShowScheduleForm(true)}
          className="flex-1 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 
            transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <Calendar className="w-4 h-4" />
          Schedule Interview
        </button>
        <button
          className="flex-1 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 
            transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <FileText className="w-4 h-4" />
          View Profile
        </button>
      </div>

      {showScheduleForm && (
        <ScheduleInterviewForm
          candidate={candidate}
          onClose={() => setShowScheduleForm(false)}
          onSubmit={handleScheduleInterview}
        />
      )}
    </motion.div>
  );
};

const StageCard = ({ stage }) => (
  <div className="bg-white rounded-xl p-6 shadow-sm">
    <div className="flex justify-between items-start mb-4">
      <div>
        <h3 className="font-semibold text-gray-900">{stage.name}</h3>
        <p className="text-2xl font-bold text-gray-800 mt-1">
          {stage.count} <span className="text-sm text-gray-500">candidates</span>
        </p>
      </div>
      <div className={`w-12 h-12 ${stage.color} rounded-xl flex items-center justify-center`}>
        {stage.icon}
      </div>
    </div>
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full ${stage.color.replace('bg-', 'bg-')}`}
        style={{ width: `${(stage.count / stage.total) * 100}%` }}
      />
    </div>
  </div>
);

const RecruitmentModule = ({ isMobile }) => {
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);

  const stages = [
    {
      name: 'Screening',
      count: 42,
      total: 100,
      color: 'bg-blue-600',
      icon: <Search className="w-6 h-6 text-white" />
    },
    {
      name: 'Interview',
      count: 18,
      total: 100,
      color: 'bg-purple-600',
      icon: <Users className="w-6 h-6 text-white" />
    },
    {
      name: 'Technical',
      count: 12,
      total: 100,
      color: 'bg-amber-600',
      icon: <FileText className="w-6 h-6 text-white" />
    },
    {
      name: 'Offer',
      count: 5,
      total: 100,
      color: 'bg-green-600',
      icon: <CheckCircle2 className="w-6 h-6 text-white" />
    }
  ];

  const candidates = [
    {
      name: 'Sarah Johnson',
      position: 'Senior Developer',
      email: 'sarah.j@example.com',
      phone: '+966 50 123 4567',
      department: 'IT',
      status: 'Interview',
      appliedDate: '2024-01-15'
    },
    {
      name: 'Mohammed Ali',
      position: 'Sales Manager',
      email: 'mohammed.a@example.com',
      phone: '+966 50 234 5678',
      department: 'Sales',
      status: 'Technical',
      appliedDate: '2024-01-14'
    }
  ];

  const handleAddCandidate = (candidateData) => {
    console.log('New candidate:', candidateData);
    setShowAddForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Departments</option>
            {employeeData.departments.map((dept, index) => (
              <option key={index} value={dept.name}>{dept.name}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Stages</option>
            <option value="screening">Screening</option>
            <option value="interview">Interview</option>
            <option value="technical">Technical</option>
            <option value="offer">Offer</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 
            flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </button>
          <button 
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
              flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Candidate
          </button>
        </div>
      </div>

      {/* Recruitment Stages */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stages.map((stage, index) => (
          <StageCard key={index} stage={stage} />
        ))}
      </div>

      {/* Candidates */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Active Candidates</h3>
          <button className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {candidates.map((candidate, index) => (
            <CandidateCard key={index} candidate={candidate} />
          ))}
        </div>
      </div>

      {/* Recruitment Analytics */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Hiring Pipeline Analytics</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stages}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {showAddForm && (
        <AddCandidateForm
          onClose={() => setShowAddForm(false)}
          onSubmit={handleAddCandidate}
        />
      )}
    </div>
  );
};

export default RecruitmentModule; 